import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";

interface Activity {
  id: number;
  name: string;
  description: string;
  price: number;
  photos: { id: number; path: string }[];
}

// ===================== COMPOSANT SKELETON =====================
const SkeletonRow = () => (
  <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
    <td style={{ padding: "14px 16px" }}>
      <div style={{ 
        width: "140px", 
        height: "18px", 
        borderRadius: "6px",
        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
      }} />
    </td>
    <td style={{ padding: "14px 16px" }}>
      <div style={{ 
        width: "200px", 
        height: "16px", 
        borderRadius: "6px",
        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
      }} />
    </td>
    <td style={{ padding: "14px 16px" }}>
      <div style={{ 
        width: "60px", 
        height: "18px", 
        borderRadius: "6px",
        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
      }} />
    </td>
    <td style={{ padding: "14px 16px", textAlign: "center" }}>
      <div style={{ 
        width: "80px", 
        height: "24px", 
        borderRadius: "12px",
        margin: "0 auto",
        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
      }} />
    </td>
    <td style={{ padding: "14px 16px", textAlign: "right" }}>
      <div style={{ display: "inline-flex", gap: "6px" }}>
        <div style={{ 
          width: "32px", 
          height: "32px", 
          borderRadius: "6px",
          background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.5s infinite",
        }} />
        <div style={{ 
          width: "32px", 
          height: "32px", 
          borderRadius: "6px",
          background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.5s infinite",
        }} />
      </div>
    </td>
  </tr>
);

const SkeletonHeader = () => (
  <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
    {["Name", "Description", "Price", "Photos Count", "Actions"].map((label, index) => (
      <th
        key={index}
        style={{
          padding: "12px 16px",
          color: "#294b5a",
          fontSize: "12px",
          fontWeight: 700,
          letterSpacing: "0.5px",
          textAlign: index === 3 ? "center" : index === 4 ? "right" : "left",
        }}
      >
        {label}
      </th>
    ))}
  </tr>
);

// ===================== COMPOSANT PRINCIPAL =====================
const ListActivities = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modals & Selection
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);

  const navigate = useNavigate();
  const API_URL_main = `${API_URL}/activities`;

  const token = localStorage.getItem("token_admin_silverjarz");

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // 1. Fetch avec Token
  const fetchActivities = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(API_URL_main, config);
      const data = response.data.data?.data ? response.data.data.data : (response.data.data || response.data);
      setActivities(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching activities:", error);
      toast.error("Error fetching activities");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, []);

  // 2. Filtrer les activités (Recherche locale)
  const filteredActivities = useMemo(() => {
    return activities.filter((activity) => {
      const term = searchTerm.toLowerCase();
      const name = activity.name || "";
      const description = activity.description || "";

      return (
        name.toLowerCase().includes(term) ||
        description.toLowerCase().includes(term)
      );
    });
  }, [activities, searchTerm]);

  // Réinitialiser la page à 1 si les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // 3. Calculs pour la pagination
  const totalPages = Math.ceil(filteredActivities.length / itemsPerPage);

  const paginatedActivities = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredActivities.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredActivities, currentPage, itemsPerPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Helper pour la pagination avec ellipses ("...")
  const getPaginationRange = (currentPage: number, totalPages: number) => {
    const delta = 1;
    const range: (number | string)[] = [];

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        range.push(i);
      } else if (
        range[range.length - 1] !== "..." &&
        (i < currentPage - delta || i > currentPage + delta)
      ) {
        range.push("...");
      }
    }
    return range;
  };

  // 4. Actions Handlers
  const handleDeleteClick = (activity: Activity) => {
    setSelectedActivity(activity);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedActivity) return;

    try {
      await axios.delete(`${API_URL_main}/${selectedActivity.id}`, config);
      toast.success("Activity deleted successfully");
      setActivities((prev) => prev.filter((a) => a.id !== selectedActivity.id));
    } catch (error: any) {
      console.error("Error deleting activity:", error);
      toast.error("Error deleting activity");
    } finally {
      setShowDeleteModal(false);
      setSelectedActivity(null);
    }
  };

  const handleEditClick = (id: number) => {
    navigate(`/admin/edit-activity/${id}`);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#f4f6f8",
        padding: "20px 20px",
        boxSizing: "border-box",
        color: "#1e293b",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* SECTION EN-TÊTE */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h1
              style={{
                color: "#294b5a",
                fontSize: "22px",
                fontWeight: 700,
                margin: 0,
                letterSpacing: "-0.02em",
              }}
            >
              Manage Activities
            </h1>
            <span
              style={{
                backgroundColor: "#294b5a",
                color: "#d3be76",
                fontSize: "11px",
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: "12px",
                letterSpacing: "0.5px",
              }}
            >
              System
            </span>
          </div>
          <p
            style={{
              color: "#64748b",
              fontSize: "13.5px",
              margin: 0,
              fontWeight: 400,
            }}
          >
            Overview and configuration of{" "}
            <strong style={{ color: "#294b5a" }}>
              {isLoading ? "..." : filteredActivities.length}
            </strong>{" "}
            of <strong>{activities.length}</strong> total activities.
          </p>
        </div>

        {/* Bouton Add Activity */}
        <button
          onClick={() => navigate("/admin/add-activity")}
          style={{
            backgroundColor: "#294b5a",
            color: "#ffffff",
            border: "none",
            borderRadius: "8px",
            padding: "10px 18px",
            fontSize: "13.5px",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: "0 2px 8px rgba(41, 75, 90, 0.25)",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "#1e3743";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(41, 75, 90, 0.35)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = "#294b5a";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(41, 75, 90, 0.25)";
          }}
        >
          <i
            className="fa-solid fa-plus"
            style={{ color: "#fff", fontSize: "13px" }}
          ></i>
          <span>Add Activity</span>
        </button>
      </div>

      {/* BLOC PRINCIPAL CONTENANT FILTRES + TABLE */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 16px -2px rgba(0,0,0,0.03)",
          overflow: "hidden",
          marginTop: "15px",
        }}
      >
        {/* BARRE DE FILTRES */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #edf2f7",
            backgroundColor: "#ffffff",
          }}
        >
          <div className="row g-3 align-items-center">
            {/* Recherche */}
            <div className="col-12 col-md-6 col-lg-5">
              <div className="position-relative">
                <input
                  type="text"
                  placeholder="Search activities by name, description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px 36px 9px 38px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    fontSize: "13px",
                    outline: "none",
                    color: "#0f172a",
                    backgroundColor: "#f8fafc",
                    transition: "all 0.2s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#294b5a";
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.boxShadow = "0 0 0 3px rgba(41, 75, 90, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#cbd5e1";
                    e.target.style.backgroundColor = "#f8fafc";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "13px",
                    color: "#94a3b8",
                  }}
                ></i>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#94a3b8",
                      padding: "0",
                    }}
                  >
                    <i
                      className="fa-solid fa-xmark"
                      style={{ fontSize: "14px" }}
                    ></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* TABLEAU */}
        {isLoading ? (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "left",
              }}
            >
              <thead>
                <SkeletonHeader />
              </thead>
              <tbody>
                {[...Array(10)].map((_, index) => (
                  <SkeletonRow key={index} />
                ))}
              </tbody>
            </table>
            
            {/* Skeleton de la barre de pagination */}
            <div
              style={{
                padding: "12px 20px",
                borderTop: "1px solid #edf2f7",
                backgroundColor: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <div style={{ fontSize: "13px", color: "#64748b" }}>
                Showing <strong>...</strong> to <strong>...</strong> of <strong>...</strong> entries
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div
                  style={{
                    padding: "6px 12px",
                    fontSize: "12.5px",
                    fontWeight: "600",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    backgroundColor: "#ffffff",
                    color: "#94a3b8",
                    width: "78px",
                    height: "32px",
                    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite",
                  }}
                />
                {[1, 2, 3, 4, 5].map((_, index) => (
                  <div
                    key={index}
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "6px",
                      background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.5s infinite",
                    }}
                  />
                ))}
                <div
                  style={{
                    padding: "6px 12px",
                    fontSize: "12.5px",
                    fontWeight: "600",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    backgroundColor: "#ffffff",
                    color: "#94a3b8",
                    width: "78px",
                    height: "32px",
                    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite",
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="table-responsive" style={{ margin: 0 }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  textAlign: "left",
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <th
                      style={{
                        padding: "12px 16px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Name
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Description
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Price
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        textAlign: "center",
                      }}
                    >
                      Photos Count
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        textAlign: "right",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedActivities.length > 0 ? (
                    paginatedActivities.map((activity) => (
                      <tr
                        key={activity.id}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          transition: "background-color 0.15s ease",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.backgroundColor = "#fafaf8")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.backgroundColor = "transparent")
                        }
                      >
                        {/* Name */}
                        <td
                          style={{
                            padding: "14px 16px",
                            fontSize: "13.5px",
                            fontWeight: 600,
                            color: "#0f172a",
                          }}
                        >
                          {activity.name}
                        </td>

                        {/* Description */}
                        <td
                          style={{
                            padding: "14px 16px",
                            fontSize: "13.5px",
                            color: "#475569",
                            maxWidth: "300px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {activity.description}
                        </td>

                        {/* Price */}
                        <td
                          style={{
                            padding: "14px 16px",
                            fontSize: "13.5px",
                            fontWeight: 600,
                            color: "#294b5a",
                          }}
                        >
                          {activity.price ? `${activity.price} AED` : "0 AED"}
                        </td>

                        {/* Photos Count */}
                        <td
                          style={{
                            padding: "14px 16px",
                            textAlign: "center",
                            fontSize: "13.5px",
                            color: "#475569",
                          }}
                        >
                          <span
                            style={{
                              backgroundColor: "#f1f5f9",
                              padding: "3px 8px",
                              borderRadius: "10px",
                              fontWeight: 600,
                            }}
                          >
                            {activity.photos?.length || 0} photos
                          </span>
                        </td>

                        {/* Actions */}
                        <td
                          style={{
                            padding: "14px 16px",
                            textAlign: "right",
                          }}
                        >
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            {/* Edit Button */}
                            <button
                              type="button"
                              onClick={() => handleEditClick(activity.id)}
                              style={{
                                border: "1px solid #e2e8f0",
                                backgroundColor: "#ffffff",
                                color: "#294b5a",
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                              }}
                              title="Edit"
                            >
                              <i
                                className="fa-solid fa-pen-to-square"
                                style={{ fontSize: "13px" }}
                              ></i>
                            </button>

                            {/* Delete Button */}
                            <button
                              type="button"
                              onClick={() => handleDeleteClick(activity)}
                              style={{
                                border: "1px solid #e2e8f0",
                                backgroundColor: "#ffffff",
                                color: "#64748b",
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                              }}
                              title="Delete"
                            >
                              <i
                                className="fa-solid fa-trash-can"
                                style={{ fontSize: "13px" }}
                              ></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          textAlign: "center",
                          padding: "48px 20px",
                          color: "#64748b",
                          fontSize: "13.5px",
                        }}
                      >
                        <i
                          className="fa-solid fa-compass mb-2"
                          style={{
                            fontSize: "24px",
                            color: "#cbd5e1",
                            display: "block",
                          }}
                        ></i>
                        No activities found matching your criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* BARRE DE PAGINATION PAR 10 */}
            {filteredActivities.length > 0 && (
              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid #edf2f7",
                  backgroundColor: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div style={{ fontSize: "13px", color: "#64748b" }}>
                  Showing{" "}
                  <strong>
                    {Math.min(
                      (currentPage - 1) * itemsPerPage + 1,
                      filteredActivities.length
                    )}
                  </strong>{" "}
                  to{" "}
                  <strong>
                    {Math.min(
                      currentPage * itemsPerPage,
                      filteredActivities.length
                    )}
                  </strong>{" "}
                  of <strong>{filteredActivities.length}</strong> entries
                </div>

                {totalPages > 1 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {/* Bouton Précédent */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      style={{
                        padding: "6px 12px",
                        fontSize: "12.5px",
                        fontWeight: "600",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        backgroundColor: "#ffffff",
                        color: currentPage === 1 ? "#94a3b8" : "#294b5a",
                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      }}
                    >
                      Previous
                    </button>

                    {/* Numéros de page avec Ellipses */}
                    {getPaginationRange(currentPage, totalPages).map(
                      (item, index) => {
                        if (item === "...") {
                          return (
                            <span
                              key={`dots-${index}`}
                              style={{
                                padding: "0 6px",
                                fontSize: "13px",
                                color: "#94a3b8",
                                fontWeight: "600",
                              }}
                            >
                              ...
                            </span>
                          );
                        }

                        const pageNum = item as number;
                        const isActive = currentPage === pageNum;

                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            style={{
                              width: "32px",
                              height: "32px",
                              fontSize: "12.5px",
                              fontWeight: "600",
                              borderRadius: "6px",
                              border: isActive ? "none" : "1px solid #cbd5e1",
                              backgroundColor: isActive ? "#294b5a" : "#ffffff",
                              color: isActive ? "#ffffff" : "#334155",
                              cursor: "pointer",
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                    )}

                    {/* Bouton Suivant */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: "6px 12px",
                        fontSize: "12.5px",
                        fontWeight: "600",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        backgroundColor: "#ffffff",
                        color: currentPage === totalPages ? "#94a3b8" : "#294b5a",
                        cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal: Confirmation de suppression */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: "16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
            }}
          >
            <div style={{ padding: "28px 24px 20px 24px", textAlign: "center" }}>
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "16px",
                }}
              >
                <i
                  className="fa-solid fa-trash-can"
                  style={{ color: "#dc2626", fontSize: "22px" }}
                ></i>
              </div>

              <h2
                style={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#294b5a",
                  margin: "0 0 8px 0",
                }}
              >
                Delete Activity?
              </h2>

              <p
                style={{
                  fontSize: "13.5px",
                  color: "#64748b",
                  margin: 0,
                }}
              >
                Are you sure you want to delete this activity? This action cannot
                be undone.
              </p>

              {selectedActivity && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px 16px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13.5px",
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    {selectedActivity.name}
                  </div>
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "16px 24px 20px 24px",
                backgroundColor: "#ffffff",
              }}
            >
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#64748b",
                  backgroundColor: "#ffffff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmDelete}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#ffffff",
                  backgroundColor: "#dc2626",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Delete Activity
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styles CSS pour l'animation shimmer */}
      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </div>
  );
};

export default ListActivities;